import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import { LoadingButton } from "@mui/lab";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormHelperText, Skeleton, Table, TableBody, TableCell, TableHead, TableRow, TextField } from "@mui/material";
import { Box } from "@mui/system";
import MainCard from "components/MainCard";
import { Notice } from "components/Notice";
import { Formik } from "formik";
import React from "react";
import { useParams } from "react-router";
import { deleteRequest, getRequest, postRequest } from "utils/apiRequest";
import * as Yup from 'yup';

const HolidaySchema = Yup.object().shape({
    name: Yup.string().max(100).required('A holiday name is required'),
    holidayDate: Yup.date().required("A holiday date is required"),
    displayStartDate: Yup.date().required("A start date is required"),
    displayEndDate: Yup.date().min(Yup.ref('displayStartDate')).required('End date must be after the start date'),
    displayTextColor: Yup.string().required('A display text color is required')
})
const HolidayAlertMessage = () => {
    return (
        <Notice title="Holidays">
            <p>Holidays can be configured per domain. The Estimated Delivery SDK can let your customers know if a product will arrive <i>before</i>, <i>on</i>, or <i>after</i> a major holiday, like Christmas or Halloween.</p>
            <p>Example: <code>Arrives before <strong>Christmas</strong></code></p>
            <p>See our <a href={process.env.REACT_APP_DOCS_URL + '/docs/js-sdk/estimated-delivery-dates/holidays'} target="_blank">holiday documentation</a> for more info</p>
        </Notice>
    );
}
const HolidayLoading = () => {

    return (
        <>
            <HolidayAlertMessage />
            <MainCard title={"Holidays"}>
                <Table>
                    <TableHead>
                        <TableRow direction="row">
                            <TableCell>Holiday Name</TableCell>
                            <TableCell>Display Text Color</TableCell>
                            <TableCell>Holiday Date</TableCell>
                            <TableCell>Display Start Date</TableCell>
                            <TableCell>Display End Date</TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {[...Array(4)].map((v, i) => {
                            <TableRow key={i}>
                                <TableCell><Skeleton type="text" width={200}></Skeleton></TableCell>
                                <TableCell><Skeleton type="text" width={200}></Skeleton></TableCell>
                                <TableCell><Skeleton type="text" width={200}></Skeleton></TableCell>
                                <TableCell><Skeleton type="text" width={200}></Skeleton></TableCell>
                                <TableCell><Skeleton type="text" width={200}></Skeleton></TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        })}
                    </TableBody>
                </Table>
            </MainCard>
        </>
    )
}

const HolidaySettings = () => {
    const { clientKey } = useParams();
    const [clientSettings, setClientSettings] = React.useState({});
    const [loading, setLoading] = React.useState(false);
    const [success, setSuccess] = React.useState(false);
    const [errors, setErrors] = React.useState([]);
    const [showAddModal, setShowAddModal] = React.useState(false);

    const getClientSettings = () => {
        setLoading(true)
        getRequest('/client/show/' + clientKey).then(data => {
            setClientSettings(data);
        }).finally(() => {
            setLoading(false);
        })
    }
    React.useEffect(() => {
        getClientSettings();
    }, [])

    const showAddButton = () => {
        return <Button onClick={() => handleAddModal()}>Add Holiday</Button>
    }
    const handleAddModal = () => {
        setSuccess(false)
        setErrors(false)
        setShowAddModal(true);
    }
    const handleAddModalClose = () => {
        setShowAddModal(false);
    }
    const handleDelete = (holidayId) => {
        var result = window.confirm("Are you sure you want to delete this?");
        if (result === true) {
            deleteRequest('/client/holiday/delete/' + holidayId).then(data => {
                getClientSettings();
            })
        }
    }
    if (loading) {
        return <HolidayLoading />
    }
    return (<>
        <HolidayAlertMessage />
        <MainCard title={"Holidays"} secondary={showAddButton()}>
            <Table>
                <TableHead>
                    <TableRow direction="row">
                        <TableCell>Holiday Name</TableCell>
                        <TableCell>Display Text Color</TableCell>
                        <TableCell>Holiday Date</TableCell>
                        <TableCell>Display Start Date</TableCell>
                        <TableCell>Display End Date</TableCell>
                        <TableCell></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {clientSettings.holidays ? (clientSettings.holidays.map((holiday, i) => {
                        return (<TableRow direction="row" key={i}>
                            <TableCell style={{ fontWeight: 'bold' }}>{holiday.name}</TableCell>
                            <TableCell>
                                <Box sx={{ backgroundColor: holiday.display_text_color, width: 32, height: 18 }} />
                            </TableCell>
                            <TableCell>
                                {holiday.holiday_date}
                            </TableCell>
                            <TableCell>
                                {holiday.display_start_datetime}
                            </TableCell>
                            <TableCell>
                                {holiday.display_end_datetime}
                            </TableCell>
                            <TableCell align="right">
                                <Button variant="outlined" color="error" onClick={() => handleDelete(holiday.id)}>
                                    <DeleteTwoToneIcon />
                                </Button>
                            </TableCell>
                        </TableRow>)
                    })) : (<></>)}
                </TableBody>
            </Table>
        </MainCard>


        <Dialog
            open={showAddModal}
            size="md"
        >
            <DialogTitle>Add Holiday</DialogTitle>
            <DialogContent>
                <Formik
                    initialValues={{
                        name: '',
                        holidayDate: (new Date()).toISOString().split('T')[0],
                        displayStartDate: (new Date()).toISOString().split('T')[0],
                        displayEndDate: (new Date()).toISOString().split('T')[0],
                        displayTextColor: '#000'
                    }}
                    validationSchema={HolidaySchema}
                    onSubmit={(values, { setErrors, setStatus, setSubmitting }) => {
                        setSuccess(false);
                        setSubmitting(true);
                        let formData = new FormData();
                        formData.append('clientId', clientSettings.id);
                        Object.keys(values).forEach((k) => {
                            formData.append(k, values[k]);
                        })
                        postRequest('/client/holiday/create', formData).then(d => {
                            if (d.errors) {
                                let err = ''
                                Object.keys(d.errors).forEach((k) => {
                                    err = d.errors[k][0];
                                })
                                setErrors(err);
                            } else {
                                setSuccess(true);
                                setShowAddModal(false);
                                getClientSettings();
                            }
                        }).catch(err => {
                            console.log(err)
                        }).finally(() => {
                            setSubmitting(false);
                        })
                    }}>
                    {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
                        <form noValidate onSubmit={handleSubmit}>
                            {success && (
                                <Alert severity="success">Holiday Added</Alert>
                            )}
                            {errors && errors.length > 0 && (
                                <Alert severity="error">{errors}</Alert>
                            )}
                            <Table>
                                <TableBody>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Holiday Display Name</TableCell>
                                        <TableCell>
                                            <TextField
                                                name="name"
                                                type="text"
                                                value={values.name}
                                                onChange={handleChange}
                                                sx={{ width: '100%' }}
                                            />
                                            {touched.name && errors.name && (
                                                <FormHelperText error>
                                                    {errors.name}
                                                </FormHelperText>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Holiday Date</TableCell>
                                        <TableCell>
                                            <TextField
                                                name="holidayDate"
                                                type="date"
                                                value={values.holidayDate}
                                                onChange={handleChange}
                                                sx={{ width: '100%' }}
                                            />
                                            {touched.holidayDate && errors.holidayDate && (
                                                <FormHelperText error>
                                                    {errors.holidayDate}
                                                </FormHelperText>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Display Start</TableCell>
                                        <TableCell>
                                            <TextField
                                                name="displayStartDate"
                                                type="date"
                                                value={values.displayStartDate}
                                                onChange={handleChange}
                                                sx={{ width: '100%' }}
                                            />
                                            {touched.displayStartDate && errors.displayStartDate && (
                                                <FormHelperText error>
                                                    {errors.displayStartDate}
                                                </FormHelperText>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Display End</TableCell>
                                        <TableCell>
                                            <TextField
                                                name="displayEndDate"
                                                type="date"
                                                value={values.displayEndDate}
                                                onChange={handleChange}
                                                sx={{ width: '100%' }}
                                            />
                                            {touched.displayEndDate && errors.displayEndDate && (
                                                <FormHelperText error>
                                                    {errors.displayEndDate}
                                                </FormHelperText>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Display Text Color</TableCell>
                                        <TableCell>
                                            <TextField
                                                name="displayTextColor"
                                                type="color"
                                                value={values.displayTextColor}
                                                onChange={handleChange}
                                                sx={{ width: '100%' }}
                                            />
                                            {touched.displayTextColor && errors.displayTextColor && (
                                                <FormHelperText error>
                                                    {errors.displayTextColor}
                                                </FormHelperText>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                            {touched.domain && errors.domain && (
                                <FormHelperText error>
                                    {errors.domain}
                                </FormHelperText>
                            )}
                            <DialogActions>
                                <Button variant="outlined" onClick={handleAddModalClose}>Close</Button>
                                <LoadingButton
                                    disableElevation
                                    disabled={isSubmitting}
                                    type="submit"
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    loading={isSubmitting}
                                >
                                    Submit
                                </LoadingButton>
                            </DialogActions>
                        </form>
                    )}
                </Formik>
            </DialogContent>
        </Dialog>
    </>)
}

export default HolidaySettings;